import BrandLogo from "assets/images/svg/click-for-marry02.svg";
import Button from "components/BaseWidgets/button";
import Switch from "components/BaseWidgets/switch";
import Upload from "components/BaseWidgets/upload";
import AlertNotification from "components/CustomWidgets/alertNotification";
import { useUploadPhoto } from "hooks/api/useOnboard";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { uploadMenu } from "../helper";
import ProfileCreatedModal from "../ProfileCreatedModal";
import UploadGuideModal from "../UploadGuideModal";
import "./style.scss";
import { retrieveToken } from "utils/storage";
import Skeleton from "components/BaseWidgets/skeleton";
import { useQueryClient } from "@tanstack/react-query";

function UploadImage({
  profileData,
  profileLoading,
  isFromProfile,
  user,
  setManagedPhoto,
  managePhotoView = false,
  imageapproved,
}) {
  const [initialState, setInitialState] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const [isPrivate, setPrivate] = useState(false);
  const [primaryIndex, setPrimaryIndex] = useState(null);
  const [fileLists, setFileLists] = useState(Array(7).fill([]));
  const [openModal, setOpenModal] = useState(true);
  const [profileCreatedModal, setProfileCreatedModal] = useState(false);
  const [primaryImagePreview, setPrimaryImagePreview] = useState();
  const [isSwapping, setIsSwapping] = useState(managePhotoView);
  const [isLoading, setIsLoading] = useState(false);
  const isBlockedRef = useRef(false);
  const [previousIsPrivate, setPreviousIsPrivate] = useState(isPrivate);
  const [isPrivacyOnlyUpdate, setIsPrivacyOnlyUpdate] = useState(false);

  useEffect(() => {
    if (!initialState) return;

    const currentFiles = JSON.stringify(
      fileLists.map((l) => l[0]?.id || l[0]?.url || null),
    );

    const changed =
      currentFiles !== initialState.files ||
      isPrivate !== initialState.isPrivate ||
      primaryIndex !== initialState.primaryIndex;

    setIsDirty(changed);
  }, [fileLists, isPrivate, primaryIndex, initialState]);

  const [imageApprovedState, setImageApprovedState] = useState(
    imageapproved || Array(7).fill(false),
  );
  const navigate = useNavigate();
  const userData = JSON.parse(retrieveToken("user") || "{}");
  const queryClient = useQueryClient();

  useEffect(() => {
    setIsSwapping(profileLoading);
  }, [profileLoading]);

  useEffect(() => {
    let images = [];
    let approvedStatuses = Array(7).fill(false);

    if (isFromProfile && user?.userImages?.length) {
      const primaryImage = user?.user?.profile_image
        ? {
            uid: "primary-image",
            id: user.user.userImages.find((img) => img.is_primary)?.id || null,
            name: "primary.jpg",
            status: "done",
            url: user.user.profile_image,
            isPrimary: true,
            isPrivate: false,
            isApproved:
              user.user.userImages.find((img) => img.is_primary)?.is_approved ||
              false,
          }
        : null;

      const secondaryImages = user.userImages.map((img, i) => ({
        uid: `uploaded-${i}`,
        id: img.id,
        name: `image-${i + 1}.jpg`,
        status: "done",
        url: img.image,
        isPrimary: false,
        isPrivate: img.is_private,
        isApproved: img.is_approved || false,
      }));

      images = primaryImage
        ? [primaryImage, ...secondaryImages]
        : [...secondaryImages];
    } else if (profileData?.user_images?.length) {
      images = profileData.user_images.map((img, i) => ({
        uid: `uploaded-${i}`,
        id: img.id,
        name: `image-${i + 1}.jpg`,
        status: "done",
        url: img.image,
        isPrimary: img.primary === 1,
        isPrivate: img.is_private,
        isApproved: img.is_approved || false,
      }));
    }

    if (!initialState && images.length === 0) {
      setInitialState({
        files: JSON.stringify(Array(7).fill(null)),
        isPrivate: false,
        primaryIndex: null,
      });
    }

    if (images.length > 0) {
      const updatedFileLists = Array(7).fill([]);

      // Find actual primary image
      const primaryImage = images.find((img) => img.isPrimary);

      //  HAVE PRIMARY  (primary = 1)
      if (primaryImage) {
        updatedFileLists[0] = [primaryImage];
        approvedStatuses[0] = primaryImage.isApproved;

        let slot = 1;
        images
          .filter((img) => !img.isPrimary)
          .forEach((img) => {
            updatedFileLists[slot] = [img];
            approvedStatuses[slot] = img.isApproved;
            slot++;
          });

        setPrimaryIndex(0);
        setPrimaryImagePreview(primaryImage.url);
      }

      //  NO PRIMARY (all primary=2)
      else {
        let slot = 1; // index 0 must remain empty
        images.forEach((img) => {
          updatedFileLists[slot] = [img];
          approvedStatuses[slot] = img.isApproved;
          slot++;
        });

        setPrimaryIndex(null);
        setPrimaryImagePreview(null);
      }

      setFileLists(updatedFileLists);
      setImageApprovedState(approvedStatuses);
      const hasPrivateImages = images.some((img) => img.isPrivate);

      const initialFiles = updatedFileLists.map(
        (l) => l[0]?.id || l[0]?.url || null,
      );

      setInitialState({
        files: JSON.stringify(initialFiles),
        isPrivate: hasPrivateImages,
        primaryIndex: primaryImage ? 0 : null,
      });
      setPrivate(hasPrivateImages);
    }
  }, [profileData, user, isFromProfile]);

  const { mutate: uploadPhotoMutate } = useUploadPhoto({
    onSuccess: (data) => {
      setOpenModal(false);

      if (!managePhotoView) {
        setProfileCreatedModal(true);
        isBlockedRef.current = false;
        setIsLoading(false);
      } else {
        setManagedPhoto(false);
        isBlockedRef.current = false;
        setIsLoading(false);
        queryClient.invalidateQueries({ queryKey: ["my-profile"] });
        queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
      }

      // Priority 1 → Privacy update message
      if (isPrivacyOnlyUpdate) {
        AlertNotification(
          "success",
          isPrivate
            ? "Photo privated successfully"
            : "Photo unprivated successfully",
        );
      } else {
        AlertNotification("success", data.message || "Successfully Updated");
      }
      setIsPrivacyOnlyUpdate(false);
    },
  });

  const handleChangeFileList = (newList, index) => {
    let newFileLists = [...fileLists];
    let newApprovedStatuses = [...imageApprovedState];

    newFileLists[index] = newList;

    if (newList.length === 0) {
      newApprovedStatuses[index] = false;
    } else {
      // Check if this is a new upload
      const file = newList[0];
      if (file.originFileObj && !file.id) {
        // New uploads are not approved
        newApprovedStatuses[index] = false;
      } else if (file.isApproved !== undefined) {
        // Existing image with approval status
        newApprovedStatuses[index] = file.isApproved;
      }
    }

    if (index === 0 && newList.length === 0) {
      const nextIndex = newFileLists.findIndex(
        (list, idx) => idx !== 0 && list.length > 0,
      );
      if (nextIndex !== -1) {
        setIsSwapping(true);
        setTimeout(() => {
          newFileLists[0] = newFileLists[nextIndex];
          newFileLists[nextIndex] = [];

          const tempApproved = newApprovedStatuses[0];
          newApprovedStatuses[0] = newApprovedStatuses[nextIndex];
          newApprovedStatuses[nextIndex] = tempApproved;

          setPrimaryIndex(0);
          const file = newFileLists[0][0];
          if (file?.originFileObj) {
            setPrimaryImagePreview(URL.createObjectURL(file.originFileObj));
          } else if (file?.url) {
            setPrimaryImagePreview(file.url);
          }
          setFileLists(newFileLists);
          setImageApprovedState(newApprovedStatuses);
          setIsSwapping(false);
        }, 400);
      } else {
        setPrimaryIndex(null);
        setPrimaryImagePreview(undefined);
        setFileLists(newFileLists);
        setImageApprovedState(newApprovedStatuses);
      }
      return;
    }

    if (primaryIndex === null && newList.length > 0) {
      const uploadedFile = newList[0];
      newFileLists[0] = [uploadedFile];
      if (index !== 0) newFileLists[index] = [];

      // Set approval status for primary
      if (uploadedFile.originFileObj && !uploadedFile.id) {
        newApprovedStatuses[0] = false;
      } else if (uploadedFile.isApproved !== undefined) {
        newApprovedStatuses[0] = uploadedFile.isApproved;
      }

      // Clear the old slot
      if (index !== 0) {
        newApprovedStatuses[index] = false;
      }

      setPrimaryIndex(0);
      if (uploadedFile.originFileObj) {
        setPrimaryImagePreview(URL.createObjectURL(uploadedFile.originFileObj));
      } else if (uploadedFile.url) {
        setPrimaryImagePreview(uploadedFile.url);
      }
      setFileLists(newFileLists);
      setImageApprovedState(newApprovedStatuses);
      return;
    }

    setFileLists(newFileLists);
    setImageApprovedState(newApprovedStatuses);

    if (index === primaryIndex && newList.length === 0) {
      setPrimaryIndex(null);
    }
  };

  const handleSubmit = () => {
    if (!isDirty) return;

    if (isBlockedRef.current) return;
    isBlockedRef.current = true;

    if (primaryIndex === null || fileLists[primaryIndex].length === 0) {
      AlertNotification(
        "warning",
        "Please select a primary image before proceeding.",
      );
      isBlockedRef.current = false;
      return;
    }

    setIsLoading(true);

    const formData = new FormData();
    const removedIds = [];
    let setAsPrimaryId = null;
    let primaryPhoto = null;

    const allFileObjects = fileLists.flat();
    const primaryFile = allFileObjects[primaryIndex];

    // Check if primary is already uploaded or new
    const isUploadedPrimary =
      primaryFile.uid?.startsWith("uploaded-") && !primaryFile.originFileObj;

    const originalPrimaryId = profileData?.user_images?.find(
      (img) => img.primary === 1,
    )?.id;

    if (isUploadedPrimary) {
      if (primaryFile.id && primaryFile.id !== originalPrimaryId) {
        setAsPrimaryId = primaryFile.id;
      }
    } else {
      primaryPhoto = primaryFile.originFileObj;
    }

    // Add other new files (not primary)
    allFileObjects.forEach((fileObj, index) => {
      if (index !== primaryIndex && fileObj?.originFileObj) {
        formData.append("files", fileObj.originFileObj);
      }
    });

    // Detect removed images
    if (profileData?.user_images?.length) {
      const currentUrls = allFileObjects.map((f) => f?.url).filter(Boolean);
      profileData.user_images.forEach((img) => {
        if (!currentUrls.includes(img.image)) {
          removedIds.push(img.id);
        }
      });
    }

    if (setAsPrimaryId) formData.append("set_as_primary_id", setAsPrimaryId);
    if (primaryPhoto) formData.append("primary_photo", primaryPhoto);
    if (removedIds.length > 0)
      formData.append("removed_ids", JSON.stringify(removedIds));
    formData.append("is_private", isPrivate);

    uploadPhotoMutate(formData);

    setTimeout(() => {
      setIsLoading(false);
      isBlockedRef.current = false;
    }, 2000);
  };

  const handleSetPrimary = (newIndex) => {
    setIsSwapping(true);

    setTimeout(() => {
      const newFileLists = [...fileLists];
      const newApprovedStatuses = [...imageApprovedState];

      const clickedItem = newFileLists[newIndex];
      const firstItem = newFileLists[0];

      newFileLists[0] = clickedItem;
      newFileLists[newIndex] = firstItem;

      // Swap imageapproved status as well
      const tempApproved = newApprovedStatuses[0];
      newApprovedStatuses[0] = newApprovedStatuses[newIndex];
      newApprovedStatuses[newIndex] = tempApproved;

      setFileLists(newFileLists);
      setImageApprovedState(newApprovedStatuses);
      setPrimaryIndex(0);

      const file = clickedItem[0];
      if (file.originFileObj) {
        setPrimaryImagePreview(URL.createObjectURL(file.originFileObj));
      } else if (file.url) {
        setPrimaryImagePreview(file.url);
      }

      setTimeout(() => setIsSwapping(false), 400);
    }, 100);
  };

  const handleModalCancel = () => {
    if (managePhotoView) {
      setManagedPhoto(false);
    } else {
      setProfileCreatedModal(false);
      navigate("/explore");
    }
  };

  const renderUpload = (index, extraClass = "") => (
    <div key={index} className={extraClass}>
      {isSwapping ? (
        <Skeleton
          label={false}
          height={index === 0 ? 370 : 170}
          className="upload-skeleton"
        />
      ) : (
        <Upload
          items={uploadMenu}
          fileList={fileLists[index]}
          onChangeFileList={(newList) => handleChangeFileList(newList, index)}
          isPrimary={primaryIndex === index}
          onSetPrimary={() => handleSetPrimary(index)}
          className={extraClass}
          imageapproved={imageApprovedState[index]}
        />
      )}
    </div>
  );

  return (
    <>
      <div className="upload-content-wrapper">
        <div className="custom-header">
          <div className="container-section container">
            {!isFromProfile && (
              <span className="logo-wrapper">
                <img className="logo" src={BrandLogo} alt="Logo" />
              </span>
            )}

            <div className="content-frame flex flex-col justify-center">
              {!isFromProfile && (
                <div className="heading-wrapper flex flex-col items-center gap-2 pb-8">
                  <h1 className="title font-title-xl">
                    Make an impression by uploading your images
                  </h1>
                  <p className="sub-title font-body-normal">
                    Profiles with 3 or more photos have 5x more chance of
                    finding your match
                  </p>
                </div>
              )}

              <div className="upload-image-wrapper flex flex-col">
                <div className="upload-set upload-wrap">
                  {[0, 1, 2, 3, 4, 5, 6].map((index) =>
                    renderUpload(
                      index,
                      `upload-wrapper upload-wrapper-${index}`,
                    ),
                  )}
                </div>

                <div className="footer-frame">
                  <div className="switch-frame">
                    <Switch
                      className={`switch`}
                      label="Set your photos as private"
                      isChecked={isPrivate}
                      onChange={(value) => {
                        setIsPrivacyOnlyUpdate(true);
                        setPreviousIsPrivate(isPrivate);
                        setPrivate(value);
                      }}
                    />
                  </div>
                  <div className="btn-wrapper flex gap-4">
                    <Button
                      className={`skip-btn-secondary`}
                      variant="secondary"
                      onClick={() => {
                        if (isFromProfile) {
                          setManagedPhoto(false);
                          return;
                        }
                        navigate("/explore");
                      }}
                    >
                      {isFromProfile ? "Cancel" : "Skip"}
                    </Button>

                    <Button
                      isLoading={isLoading}
                      isDisabled={!isDirty}
                      onClick={handleSubmit}
                      className={`save-btn-primary`}
                      variant="primary"
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!isFromProfile && (
        <UploadGuideModal
          open={openModal}
          onCancel={() => setOpenModal(false)}
          onOk={() => setOpenModal(false)}
          title="Upload a photo"
          description="You can now complete your profile."
        />
      )}

      <ProfileCreatedModal
        className="profile-created-modal"
        wrapClassName={`profile-created-modal`}
        width={482}
        src={primaryImagePreview}
        open={profileCreatedModal}
        onCancel={handleModalCancel}
        onOk={() => setProfileCreatedModal(false)}
        title={
          <>
            Congratulations! <br />
            You have created a profile
          </>
        }
        aec_id={userData.aec_id}
      />
    </>
  );
}

export default UploadImage;
